import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { PortfolioForecastWidget } from "../portfolio-forecast-widget";
import { T } from "./portfolio-forecast.i18n";

type PortfolioForecastProps = {
  portfolioId: string | null;
  config: {
    style?: string;
    title?: string;
    description?: string;
    showStatistics?: boolean;
    showTable?: boolean;
    compareWithBank?: boolean;
  };
  language: string;
  /**
   * Phase 2 S6 — engine reported `kind: "snapshot"` for this component on this portfolio.
   * When set AND prerequisites are met (D31 `hasForecastPrerequisites` — portfolio
   * decision exists + (custom mode OR RA done)) but the forecast snapshot row is
   * missing, replace the "Forecast data not available" fallback with a clear
   * snapshot-missing placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
};

export function PortfolioForecast({
  portfolioId,
  config,
  language,
  hasValidationError = false,
}: PortfolioForecastProps) {
  const t = pickT(T, language);
  const forecast = usePortfolioSnapshot("forecast", portfolioId);
  const assetAllocation = usePortfolioSnapshot("assetAllocation", portfolioId);

  if (forecast.isLoading || assetAllocation.isLoading) return null;

  if (!forecast.data) {
    if (!assetAllocation.data) {
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.portfolioSelectionRequired}
        </div>
      );
    }
    // S6 — D31 N/A means the !assetAllocation.data branch above wouldn't trigger
    // validation. Reaching this fallback with hasValidationError=true means prereqs
    // are met but the forecast snapshot row is missing (system-bug case).
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.forecastDataNotAvailable}
      </div>
    );
  }

  const snapshot =
    typeof forecast.data === "string"
      ? (() => {
          try {
            return JSON.parse(forecast.data as string);
          } catch {
            return null;
          }
        })()
      : forecast.data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoadingForecast}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 space-y-2">
        <div className="text-sm font-medium text-destructive">{t.forecastUnavailable}</div>
        <div className="text-xs text-muted-foreground">{String(snapshot.error)}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <PortfolioForecastWidget
        snapshot={snapshot}
        showStatistics={config.showStatistics !== false}
        showTable={config.showTable !== false}
        compareWithBank={config.compareWithBank === true}
        language={language}
      />
    </VisualizationContainer>
  );
}
